/**
 * loadingGate.ts — defensive watchdog + debug telemetry for any UI gate
 * that depends on async state (Firestore subscriptions, lazy chunks,
 * auth init, etc).
 *
 * Why this exists
 * ──────────────────────────────────────────────────────────────────────
 * On 2026-05-09 a live demo on prest0.ai stalled on a lowercase
 * "loading…" Suspense fallback because Miles's browser extension blocked
 * the Firestore real-time long-poll
 * (`firestore.googleapis.com/.../Listen/channel`) with
 * `net::ERR_BLOCKED_BY_CLIENT`. The one-shot hydration (`getDoc`/`getDocs`)
 * had already completed, but the page still hung because something
 * downstream was implicitly waiting on the subscription.
 *
 * The principle: a user-facing app must NEVER hang on loading more than a
 * few seconds, regardless of what's broken on the server side. If a gate
 * stays "not ready" past its budget, the watchdog flips it ready, logs a
 * warning, and lets the user see the UI. Hydrated data is the source of
 * truth for first paint; the live subscription is only a nice-to-have for
 * cross-tab sync.
 *
 * Staged budgets (revised 2026-05-10, fix/splash-overlay-overtrigger)
 * ──────────────────────────────────────────────────────────────────────
 * Real-world residential bundles routinely take 4–10 s to mount (5+ MB
 * main bundle, parse + eval + first React commit). Coupling user-visible
 * failure UI to a single 4 s hard timer false-fires on most pages on slow
 * networks. The codebase now uses three distinct budgets, each with a
 * different shape:
 *
 *   • `LOADING_GATE_DEFAULT_TIMEOUT_MS` (4 s) — silent log-only
 *     diagnostic budget for the App-level loading watchdog
 *     (`AppContent` in `src/App.tsx`) and `useLoadingWatchdog` (which
 *     also auto-unblocks the gate). NEVER renders a failure UI.
 *
 *   • `LOADING_GATE_SOFT_HINT_MS` (6 s) — when a Suspense fallback is
 *     still rendering, swap copy from `loading…` to `still loading…`.
 *     This is a low-urgency progress signal, NOT a failure UI. No
 *     refresh button. Visual style identical to the default.
 *
 *   • `LOADING_GATE_TERMINAL_TIMEOUT_MS` (30 s) — only at this point
 *     do we treat a still-rendering Suspense fallback as evidence of a
 *     real failure and surface the recoverable
 *     "this page is taking longer than expected to load. refresh"
 *     UI. By 30 s a chunk-load failure or hung gate is the only
 *     realistic explanation.
 *
 * Public API
 * ──────────────────────────────────────────────────────────────────────
 * `useLoadingWatchdog(isReady, gateName, timeoutMs?)` — returns an
 *   `effectiveReady` boolean which is `true` as soon as `isReady` is true
 *   OR the watchdog timer has fired. Default timeout: 4000 ms — this
 *   hook auto-unblocks downstream gates (it does NOT render failure UI),
 *   so the 4 s default is correct for it.
 *
 * `logLoadingGate(gateName, isReady, waitingOn?)` — opt-in render-time
 *   logger. Activated by setting `localStorage.DEBUG_LOADING_GATE = '1'`
 *   in DevTools. Helps diagnose this class of hang in five seconds the
 *   next time it shows up.
 *
 * `computeFallbackStage(elapsedMs, softHintMs, terminalMs)` — pure
 *   helper that maps elapsed time to the staged fallback stage
 *   (`'idle' | 'softHint' | 'stuck'`). Extracted so the staged-fallback
 *   semantics are unit-testable without React.
 *
 * `LOADING_GATE_DEFAULT_TIMEOUT_MS`, `LOADING_GATE_SOFT_HINT_MS`,
 *   `LOADING_GATE_TERMINAL_TIMEOUT_MS` — exported so call sites and
 *   tests reference the same constants.
 */

import { useEffect, useState } from 'react';

export const LOADING_GATE_DEFAULT_TIMEOUT_MS = 4000;

/**
 * When a Suspense fallback is still rendering at this elapsed time,
 * swap the copy from `loading…` to `still loading…`. Low-urgency
 * progress signal — NOT a failure UI, no refresh button. Tuned from
 * empirical residential mobile loads where the main bundle parse +
 * first React commit lands in the 4–8 s band on the slow tail.
 */
export const LOADING_GATE_SOFT_HINT_MS = 6000;

/**
 * When a Suspense fallback is *still* rendering at this elapsed time,
 * surface the recoverable "this page is taking longer than expected to
 * load. refresh" UI. By 30 s, a real chunk-load failure (404, parse
 * error, ERR_BLOCKED_BY_CLIENT) or a hard hang (DNS/TLS, throttled
 * background tab) is the only realistic explanation — false-firing on
 * a healthy-but-slow load is statistically negligible at this budget.
 */
export const LOADING_GATE_TERMINAL_TIMEOUT_MS = 30000;

/**
 * Pure helper: given the current upstream `isReady` flag and whether
 * the watchdog has already fired, decide whether the consumer should
 * treat the gate as ready.
 *
 * Extracted so the core decision can be unit-tested without React /
 * jsdom. Mirrors `useLoadingWatchdog`'s effective-ready calculation.
 */
export function computeEffectiveReady(
  isReady: boolean,
  watchdogFired: boolean,
): boolean {
  return isReady || watchdogFired;
}

/**
 * Pure helper: should the watchdog timer be (re)scheduled given the
 * current state? The timer should run only when the gate is not yet
 * ready and the watchdog has not already fired.
 */
export function shouldScheduleWatchdog(
  isReady: boolean,
  watchdogFired: boolean,
): boolean {
  return !isReady && !watchdogFired;
}

/**
 * Stages used by the staged Suspense fallback. The fallback transitions
 * monotonically `idle → softHint → stuck`; once the stuck stage is
 * reached it stays there for the lifetime of the fallback (re-mounting
 * the Suspense boundary resets it).
 */
export type FallbackStage = 'idle' | 'softHint' | 'stuck';

/**
 * Pure helper: given elapsed-since-mount and the two staged budgets,
 * compute the current fallback stage.
 *
 * Contract:
 *   • elapsedMs <  softHintMs                    → 'idle'
 *   • softHintMs <= elapsedMs < terminalMs       → 'softHint'
 *   • elapsedMs >= terminalMs                    → 'stuck'
 *
 * If the budgets are misordered (terminal <= softHint), the function
 * still returns the most-advanced stage that the elapsed time qualifies
 * for, so a misconfiguration cannot leave the user stranded — but a
 * console.warn-worthy misconfiguration is the caller's responsibility,
 * not this helper's.
 */
export function computeFallbackStage(
  elapsedMs: number,
  softHintMs: number = LOADING_GATE_SOFT_HINT_MS,
  terminalMs: number = LOADING_GATE_TERMINAL_TIMEOUT_MS,
): FallbackStage {
  if (elapsedMs >= terminalMs) return 'stuck';
  if (elapsedMs >= softHintMs) return 'softHint';
  return 'idle';
}

/**
 * Render-time debug logger.
 *
 * Gated behind `localStorage.DEBUG_LOADING_GATE = '1'` so it costs
 * essentially nothing in production but gives instant diagnostics when
 * something hangs again.
 */
export function logLoadingGate(
  gateName: string,
  isReady: boolean,
  waitingOn?: string | string[],
): void {
  try {
    if (typeof window === 'undefined') return;
    const ls = window.localStorage;
    if (!ls) return;
    if (ls.getItem('DEBUG_LOADING_GATE') !== '1') return;

    const waiting = Array.isArray(waitingOn)
      ? waitingOn.join(',')
      : (waitingOn || (isReady ? '<none>' : '<unspecified>'));

    // eslint-disable-next-line no-console
    console.log(
      `[loadingGate] gate=${gateName} isReady=${String(isReady)} waitingOn=${waiting}`,
    );
  } catch {
    // Never let a debug logger crash the app.
  }
}

/**
 * Hook that returns an `effectiveReady` flag.
 *
 * - Flips true immediately when the upstream `isReady` is true.
 * - If `isReady` is still false after `timeoutMs`, the watchdog flips
 *   `effectiveReady` to true anyway and logs a `console.warn` so the
 *   issue is visible in the user's DevTools.
 *
 * The watchdog is fire-and-forget — once it has flipped, it stays flipped
 * for the remainder of the component's lifetime, even if `isReady`
 * later flickers back to false. Re-mounting the component resets it.
 *
 * Note: this hook auto-unblocks downstream gates (it does NOT render any
 * user-visible failure UI), so the 4 s default budget is correct for it.
 * For staged user-visible Suspense fallbacks see
 * `LOADING_GATE_SOFT_HINT_MS` / `LOADING_GATE_TERMINAL_TIMEOUT_MS` and
 * `computeFallbackStage`.
 */
export function useLoadingWatchdog(
  isReady: boolean,
  gateName: string,
  timeoutMs: number = LOADING_GATE_DEFAULT_TIMEOUT_MS,
): boolean {
  const [watchdogFired, setWatchdogFired] = useState(false);

  useEffect(() => {
    if (!shouldScheduleWatchdog(isReady, watchdogFired)) return;
    const handle = window.setTimeout(() => {
      // eslint-disable-next-line no-console
      console.warn(
        `[loadingGate:watchdog] gate="${gateName}" did not become ready in ${timeoutMs}ms — ` +
          'unblocking UI. Likely cause: a Firestore real-time subscription was blocked, ' +
          'rate-limited, or otherwise slow (for example net::ERR_BLOCKED_BY_CLIENT from a ' +
          'browser extension blocking firestore.googleapis.com/.../Listen/channel). ' +
          'Hydrated state is being used to render; cross-tab sync will resume when the ' +
          'subscription recovers.',
      );
      setWatchdogFired(true);
    }, timeoutMs);
    return () => window.clearTimeout(handle);
  }, [isReady, watchdogFired, gateName, timeoutMs]);

  const effectiveReady = computeEffectiveReady(isReady, watchdogFired);

  // Render-time telemetry. Cheap when DEBUG flag is off.
  logLoadingGate(gateName, effectiveReady, isReady ? '<none>' : 'upstream-not-ready');

  return effectiveReady;
}
